import React, { useState } from 'react';
import {
  ButtonDropdown,
  ButtonDropdownProps,
  NonCancelableCustomEvent,
  SelectProps,
} from '@amzn/awsui-components-core';
import ButtonDropdownSection from '../../model/ButtonDropdownSection';
import CustomFilterFormModal from './CustomFilterFormModal';

interface Props {
  onSaveFilter: () => void;
  onSaveAsFilter: (filterName: string, isDefault?: boolean) => string;
  onSetDefaultFilter: () => void;
  onDeleteFilter: () => void;
  onRenameFilter: (filterName: string) => string;
  onValidateCreateNew: () => boolean;
  onValidateDelete: () => boolean;
  onValidateRename: () => boolean;
  selectedFilter?: SelectProps.Option;
}

export enum CustomFilterActionEnum {
  Save = 'Save',
  SaveAs = 'SaveAs',
  Default = 'Default',
  Delete = 'Delete',
  Rename = 'Rename',
}

const CustomFilterActionButtonDropdown: React.FunctionComponent<Props> = (props: Props) => {
  const {
    onSaveFilter,
    onSaveAsFilter,
    onSetDefaultFilter,
    onDeleteFilter,
    onRenameFilter,
    onValidateCreateNew,
    onValidateDelete,
    onValidateRename,
    selectedFilter,
  } = props;
  const [isSaveAsModalVisible, setIsSaveAsModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const onDismiss = (modalType: CustomFilterActionEnum) => {
    switch (modalType) {
      case CustomFilterActionEnum.SaveAs:
        setIsSaveAsModalVisible(false);
        break;
      case CustomFilterActionEnum.Rename:
        setIsRenameModalVisible(false);
        break;
      case CustomFilterActionEnum.Delete:
        setIsDeleteModalVisible(false);
        break;
    }
  };

  const onSubmit = (
    modalType: CustomFilterActionEnum,
    filterName?: string,
    isDefault?: boolean,
  ) => {
    let validationError = '';
    switch (modalType) {
      case CustomFilterActionEnum.SaveAs:
        if (onSaveAsFilter && filterName) {
          validationError = onSaveAsFilter(filterName, isDefault);
        }
        setIsSaveAsModalVisible(!!validationError ? true : false);
        break;
      case CustomFilterActionEnum.Rename:
        if (onRenameFilter && filterName) {
          validationError = onRenameFilter(filterName);
        }
        setIsRenameModalVisible(!!validationError ? true : false);
        break;
      case CustomFilterActionEnum.Delete:
        if (onDeleteFilter) {
          onDeleteFilter();
        }
        setIsDeleteModalVisible(false);
        break;
      default:
        validationError = 'Invalid action';
        break;
    }
    return validationError;
  };

  const getActionItems = () => {
    const actionItems: ButtonDropdownSection[] = [
      {
        id: 'Actions',
        disabled: false,
        text: 'Actions',
        items: [
          {
            id: CustomFilterActionEnum.Save,
            text: 'Save',
            disabled: !selectedFilter,
          },
          {
            id: CustomFilterActionEnum.SaveAs,
            text: 'Save as',
          },
          {
            id: CustomFilterActionEnum.Default,
            text: 'Set as default',
            disabled: !selectedFilter,
          },
          {
            id: CustomFilterActionEnum.Delete,
            text: 'Delete',
            disabled: !selectedFilter,
          },
          {
            id: CustomFilterActionEnum.Rename,
            text: 'Rename',
            disabled: !selectedFilter,
          },
        ],
      },
    ];
    return actionItems;
  };

  const handleAction = async (
    event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const eventId = event.detail.id;
    switch (eventId) {
      case CustomFilterActionEnum.Save:
        onSaveFilter();
        break;
      case CustomFilterActionEnum.SaveAs:
        if (onValidateCreateNew()) {
          setIsSaveAsModalVisible(true);
        }
        break;
      case CustomFilterActionEnum.Default:
        onSetDefaultFilter();
        break;
      case CustomFilterActionEnum.Delete:
        if (onValidateDelete()) {
          setIsDeleteModalVisible(true);
        }
        break;
      case CustomFilterActionEnum.Rename:
        if (onValidateRename()) {
          setIsRenameModalVisible(true);
        }
        break;
    }
  };

  return (
    <>
      <ButtonDropdown items={getActionItems()} variant="icon" onItemClick={handleAction} />
      <CustomFilterFormModal
        formType={CustomFilterActionEnum.SaveAs}
        onSubmit={(filterName, isDefault) =>
          onSubmit(CustomFilterActionEnum.SaveAs, filterName, isDefault)
        }
        onDismiss={() => onDismiss(CustomFilterActionEnum.SaveAs)}
        visible={isSaveAsModalVisible}
      />
      <CustomFilterFormModal
        defaultFilterName={selectedFilter?.label}
        formType={CustomFilterActionEnum.Rename}
        onSubmit={(filterName) => onSubmit(CustomFilterActionEnum.Rename, filterName)}
        onDismiss={() => onDismiss(CustomFilterActionEnum.Rename)}
        visible={isRenameModalVisible}
      />
      <CustomFilterFormModal
        defaultFilterName={selectedFilter?.label}
        formType={CustomFilterActionEnum.Delete}
        onSubmit={() => onSubmit(CustomFilterActionEnum.Delete)}
        onDismiss={() => onDismiss(CustomFilterActionEnum.Delete)}
        visible={isDeleteModalVisible}
      />
    </>
  );
};

export default CustomFilterActionButtonDropdown;
